import {
	type ErrorComponentProps,
	Link,
	useRouter,
} from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Home, RefreshCcw } from "lucide-react";
import { Environment, envVars } from "~/lib/env/schema";
import { cn } from "~/lib/utils";
import { Button } from "./ui/button";

export class EventPassedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "EventPassedError";
	}
}

export function ErrorComponent({ error, info, reset }: ErrorComponentProps) {
	const router = useRouter();
	const isAuthError =
		error instanceof Error &&
		(error.message.toLowerCase().includes("unauthorized") ||
			error.message.toLowerCase().includes("unauthenticated"));

	const isNotFoundError =
		error instanceof Error && error.message.toLowerCase().includes("not found");

	const isEventPassedError = error instanceof EventPassedError;

	return (
		<div className="min-h-screen bg-gradient-to-b from-black to-[#0F2231] flex items-center justify-center p-4">
			<div className="w-full max-w-md">
				<div className="text-center">
					<div className="bg-red-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
						<AlertCircle className="w-8 h-8 text-red-500" />
					</div>

					<h1 className="text-2xl font-bold text-white mb-2">
						{isAuthError
							? "Access Denied"
							: isNotFoundError
								? "Page Not Found"
								: isEventPassedError
									? "Event Has Passed"
									: "Something went wrong"}
					</h1>

					<p className="text-white/60 mb-8">
						{isAuthError
							? "You don't have permission to view this page. Please sign in to continue."
							: isNotFoundError
								? "The page you're looking for doesn't exist or has been moved."
								: error instanceof Error
									? error.message
									: "An unexpected error occurred. Please try again later."}
					</p>

					<div className="space-y-3">
						{/* Show reset/refresh button for general errors */}
						{!isAuthError && !isNotFoundError && (
							<Button
								size="lg"
								className={cn(
									"w-full text-base font-medium rounded-2xl",
									"bg-accent text-accent-foreground",
									"hover:bg-accent/90 transition-all duration-200",
									"active:scale-[0.98]",
									"shadow shadow-accent/25",
								)}
								onClick={
									typeof reset === "function"
										? reset
										: () => window.location.reload()
								}
							>
								<RefreshCcw className="w-4 h-4 mr-2" />
								{typeof reset === "function" ? "Reset" : "Try Again"}
							</Button>
						)}

						{/* Back button */}
						<Button
							size="lg"
							className={cn(
								"w-full text-base font-medium rounded-2xl",
								"border-white/10 bg-white/5 text-white/70 backdrop-blur-sm",
								"hover:bg-white/[0.07] hover:border-white/20 hover:text-white transition-all duration-200",
								"active:scale-[0.98]",
							)}
							onClick={() => router.history.back()}
						>
							<ArrowLeft className="w-4 h-4 mr-2" />
							Go Back
						</Button>

						{/* Home button */}
						<Button
							size="lg"
							className={cn(
								"w-full text-base font-medium rounded-2xl",
								"border-white/10 bg-white/5 text-white/70 backdrop-blur-sm",
								"hover:bg-white/[0.07] hover:border-white/20 hover:text-white transition-all duration-200",
								"active:scale-[0.98]",
							)}
							asChild
						>
							<Link to="/">
								<Home className="w-4 h-4 mr-2" />
								Back to Home
							</Link>
						</Button>
					</div>

					{/* Show error details in development */}
					{envVars.VITE_APP_ENV === Environment.development && info ? (
						<div className="mt-8 p-4 bg-white/5 rounded-xl text-left">
							<details className="text-white/60 text-sm">
								<summary className="cursor-pointer hover:text-white transition-colors">
									Error Details
								</summary>
								<pre className="mt-2 whitespace-pre-wrap text-xs">
									{JSON.stringify(info, null, 2)}
								</pre>
							</details>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
